import { useEffect, useMemo, useState } from 'react';

import { Box, type SxProps, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { useGoogleMapsApi } from '@modules/_shared/hooks/useGoogleMapsApi';
import { useLocale } from '@shared/hooks/locale';

import { type PropertyMapMode } from './property-map-types';
import {
  INSET_ZOOM,
  PREVIEW_SATELLITE_ZOOM,
  SATELLITE_ZOOM,
  Satellite3DPane,
  StreetViewPane,
  TwoDimensionalMapPane,
  ZoningMapPane,
} from './PropertyMapPanes';

type PropertyMapViewerProps = {
  lat: number;
  lng: number;
  hideMarker?: boolean;
  initialMode?: PropertyMapMode;
  modeOrder: PropertyMapMode[];
  defaultZoom?: number;
  readOnly?: boolean;
  showControls?: boolean;
  showInset?: boolean;
  controlSize?: number;
  showCameraControl?: boolean;
};

export const PropertyMapViewer = ({
  lat,
  lng,
  hideMarker = false,
  initialMode = 'map',
  modeOrder,
  defaultZoom = SATELLITE_ZOOM,
  readOnly = false,
  showControls = true,
  showInset = false,
  controlSize,
  showCameraControl = true,
}: PropertyMapViewerProps) => {
  const theme = useTheme();
  const { t } = useLocale();
  const mapApi = useGoogleMapsApi(true);

  const hasCoordinates = lat != null && lng != null;
  const showMarker = hasCoordinates && showControls && !hideMarker;
  const isReadOnly = readOnly || !showControls;

  const [mode, setMode] = useState<PropertyMapMode>(initialMode);
  const [panoramaData, setPanoramaData] =
    useState<google.maps.StreetViewPanoramaData | null>(null);
  const [hasStreetView, setHasStreetView] = useState(true);

  useEffect(() => {
    let isMounted = true;

    if (!mapApi || !hasCoordinates || !mapApi.StreetViewService) {
      setHasStreetView(false);
      setPanoramaData(null);
      return;
    }

    const service = new mapApi.StreetViewService();
    service.getPanorama(
      { location: { lat, lng }, radius: 50 },
      (
        data: google.maps.StreetViewPanoramaData | null,
        status: google.maps.StreetViewStatus,
      ) => {
        if (!isMounted) {
          return;
        }

        const isAvailable = status === mapApi.StreetViewStatus.OK;
        setHasStreetView(isAvailable);
        setPanoramaData(isAvailable ? data : null);
      },
    );

    return () => {
      isMounted = false;
    };
  }, [mapApi, hasCoordinates, lat, lng]);

  const availableModes = useMemo(() => {
    const labels: Record<PropertyMapMode, string> = {
      map: t('Map'),
      satellite: t('Satellite'),
      '3d': t('3D'),
      streetView: t('Street View'),
      zoning: t('Zoning'),
    };

    return modeOrder
      .filter(modeOption => {
        if (modeOption === 'streetView') {
          return hasCoordinates && hasStreetView;
        }
        return true;
      })
      .map(value => ({ label: labels[value], value }));
  }, [hasCoordinates, hasStreetView, modeOrder, t]);

  useEffect(() => {
    if (mode === 'streetView' && !hasCoordinates) {
      setMode('map');
      return;
    }
    if (!availableModes.some(option => option.value === mode)) {
      setMode(availableModes[0]?.value ?? 'map');
    }
  }, [availableModes, hasCoordinates, mode]);

  if (!mapApi) {
    return null;
  }

  const mapCenter = { lat, lng };
  const zoom = isReadOnly ? PREVIEW_SATELLITE_ZOOM : defaultZoom;

  const main2DMap = (
    <TwoDimensionalMapPane
      center={mapCenter}
      defaultZoom={zoom}
      mapTypeId={mode === 'map' ? 'roadmap' : 'satellite'}
      mapsApi={mapApi}
      readOnly={isReadOnly}
      showMarker={showMarker}
      controlSize={controlSize}
      showCameraControl={showCameraControl}
    />
  );

  const renderMap = () => {
    if (mode === 'zoning') {
      return <ZoningMapPane lat={lat} lng={lng} />;
    }

    if (mode === 'streetView') {
      return (
        <StreetViewPane
          lat={lat}
          lng={lng}
          mapsApi={mapApi}
          panoramaData={panoramaData}
        />
      );
    }

    return (
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        {mode === '3d' && hasCoordinates ? (
          <Satellite3DPane
            lat={lat}
            lng={lng}
            mapsApi={mapApi}
            readOnly={isReadOnly}
            showMarker={showMarker}
            fallback={main2DMap}
          />
        ) : (
          main2DMap
        )}

        {showInset && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              width: { xs: 118, md: 148 },
              height: { xs: 118, md: 148 },
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: theme.shadows[4],
              border: `1px solid ${(theme.vars ?? theme).palette.grey[300]}`,
              bgcolor: (theme.vars ?? theme).palette.background.paper,
              pointerEvents: 'none',
            }}
          >
            <TwoDimensionalMapPane
              center={mapCenter}
              defaultZoom={INSET_ZOOM}
              mapTypeId={
                mode === 'satellite' || mode === '3d' ? 'roadmap' : 'satellite'
              }
              mapsApi={mapApi}
              readOnly
              showMarker={showMarker}
              inset
            />
          </Box>
        )}
      </Box>
    );
  };

  const toggleSx: SxProps<Theme> = {
    backgroundColor: (theme.vars ?? theme).palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: '8px',
    '& .MuiToggleButton-root': {
      px: 1.5,
      py: 0.5,
      borderRadius: '8px !important',
      '&.Mui-selected': {
        backgroundColor: (theme.vars ?? theme).palette.grey[100],
        fontWeight: 600,
      },
    },
  };

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>{renderMap()}</Box>

      {showControls && availableModes.length > 1 && (
        <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10 }}>
          <SegmentedToggleButtonGroup
            value={mode}
            onChange={setMode}
            options={availableModes}
            disableRipple
            sx={toggleSx}
          />
        </Box>
      )}
    </Box>
  );
};
